// frontend/src/components/Sidebar/Sidebar.js

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { logout, getUserRole, getUserData, getLocalUserData } from '../../utils/auth';
import { useJefeMetrics } from '../../hooks/useJefeMetrics';
import { FaTachometerAlt, FaSignOutAlt, FaDollarSign, FaUserPlus, FaProjectDiagram, FaCog, FaFileInvoiceDollar, FaUserCircle, FaInbox, FaClipboardCheck } from 'react-icons/fa';
import './Sidebar.css';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  // Sidebar hover-only: siempre colapsado por defecto, se expande con :hover via CSS
  const collapsed = true;
  // Pasamos el rol para que el hook evite llamadas si no es JEFE
  const jefeMetrics = useJefeMetrics(userRole);

  useEffect(() => {
    const fetchUserData = async () => {
      const role = await getUserRole();
      setUserRole(role);

      const data = await getUserData();
      let name = '';

      if (data) {
        name = data.nombre || '';
        // Si id_empresa es null, es SuperAdmin
        if (data.id_empresa === null) {
          setIsSuperAdmin(true);
        }
      } else {
        const local = getLocalUserData();
        name = local.userName || '';
      }
      setUserName(name);
    };
    fetchUserData();
  }, []);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      alert('Ocurrió un error al cerrar sesión');
    }
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  // Determinar rutas dinámicas según el rol
  const getDashboardPath = () => {
    // Prioridad: si es SuperAdmin, siempre ir a su dashboard
    if (isSuperAdmin) return '/app/superadmin/dashboard';

    if (!userRole) return '/app/colaborador/dashboard'; // Default mientras carga

    const roleMap = {
      'COLABORADOR': '/app/colaborador/dashboard',
      'JEFE': '/app/jefe/invitaciones',
      'ADMIN': '/app/admin/dashboard'
    };
    return roleMap[userRole] || '/app/colaborador/dashboard';
  };

  const dashboardPath = getDashboardPath();
  const roleLabel = (r) => {
    if (isSuperAdmin) return 'Super Admin';
    const map = {
      'ADMIN': 'Administrador',
      'JEFE': 'Jefe de Proyecto',
      'COLABORADOR': 'Colaborador'
    };
    return map[r] || (r ? r : '—');
  };

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-brand">
        <div className="brand-lockup" title="CommitFlow" aria-label="CommitFlow">
          <div className="brand-initial" aria-hidden="true">
            <span>C</span>
          </div>
          <div className="brand-text">
            <span className="brand-name">CommitFlow</span>
            <span className="brand-sub">dashboard</span>
            {!!userName && (
              <div className="sidebar-user-card">
                <span className="sidebar-user-name">{userName}</span>
                <span className={`sidebar-role-badge role-${String(userRole || 'ADMIN').toLowerCase()}`}>
                  {roleLabel(userRole)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Sin controles de colapso: solo efecto hover */}

      <nav className="sidebar-nav">
        <Link
          to="/app/perfil"
          className={`nav-item ${location.pathname === '/app/perfil' ? 'active' : ''}`}
          title="Perfil"
          data-label="Perfil"
        >
          <FaUserCircle />
          <span>Perfil</span>
        </Link>

        {/* Link Inicio (para COLABORADOR, ADMIN y SUPERADMIN) */}
        {userRole !== 'JEFE' && (
          <Link
            to={dashboardPath}
            className={`nav-item ${location.pathname === dashboardPath ? 'active' : ''}`}
            title="Inicio"
            data-label="Inicio"
          >
            <FaTachometerAlt />
            <span>Inicio</span>
          </Link>
        )}

        {/* Mis Invitaciones para colaboradores */}
        {userRole === 'COLABORADOR' && (
          <Link
            to="/app/colaborador/invitaciones"
            className={`nav-item ${location.pathname === '/app/colaborador/invitaciones' ? 'active' : ''}`}
            title="Mis Invitaciones"
            data-label="Mis Invitaciones"
          >
            <FaInbox />
            <span>Mis Invitaciones</span>
          </Link>
        )}

        {userRole === 'COLABORADOR' && (
          <Link
            to="/app/colaborador/proyectos"
            className={`nav-item ${location.pathname === '/app/colaborador/proyectos' ? 'active' : ''}`}
            title="Mis Proyectos"
            data-label="Mis Proyectos"
          >
            <FaProjectDiagram />
            <span>Mis Proyectos</span>
          </Link>
        )}


        {/* Mostrar "Mis Pagos" solo para colaboradores */}
        {userRole === 'COLABORADOR' && (
          <Link
            to="/app/colaborador/pagos"
            className={`nav-item ${location.pathname === '/app/colaborador/pagos' ? 'active' : ''}`}
            title="Mis Pagos"
            data-label="Mis Pagos"
          >
            <FaDollarSign />
            <span>Mis Pagos</span>
          </Link>
        )}

        {/* Mostrar opciones de Admin */}
        {userRole === 'ADMIN' && (
          <>
            <Link
              to="/app/admin/suscripcion"
              className={`nav-item ${location.pathname === '/app/admin/suscripcion' ? 'active' : ''}`}
              title="Suscripción"
              data-label="Suscripción"
            >
              <FaDollarSign />
              <span>Suscripción</span>
            </Link>
            <Link
              to="/app/admin/usuarios"
              className={`nav-item ${location.pathname === '/app/admin/usuarios' ? 'active' : ''}`}
              title="Usuarios"
              data-label="Usuarios"
            >
              <FaUserPlus />
              <span>Usuarios</span>
            </Link>
            <Link
              to="/app/admin/proyectos"
              className={`nav-item ${location.pathname === '/app/admin/proyectos' ? 'active' : ''}`}
              title="Proyectos"
              data-label="Proyectos"
            >
              <FaProjectDiagram />
              <span>Proyectos</span>
            </Link>
            <Link
              to="/app/admin/reportes-proyectos"
              className={`nav-item ${location.pathname === '/app/admin/reportes-proyectos' ? 'active' : ''}`}
              title="Informe de proyectos"
              data-label="Informe de proyectos"
            >
              <FaProjectDiagram />
              <span>Informe de proyectos</span>
            </Link>
            <Link
              to="/app/admin/configuracion"
              className={`nav-item ${location.pathname === '/app/admin/configuracion' ? 'active' : ''}`}
              title="Configuración"
              data-label="Configuración"
            >
              <FaCog />
              <span>Configuración</span>
            </Link>
            <Link
              to="/app/admin/reportes"
              className={`nav-item ${location.pathname === '/app/admin/reportes' ? 'active' : ''}`}
              title="Informes de pagos"
              data-label="Informes de pagos"
            >
              <FaFileInvoiceDollar />
              <span>Informes de pagos</span>
            </Link>
          </>
        )}

        {/* Mostrar opciones específicas del Jefe */}
        {userRole === 'JEFE' && (
          <>
            <Link
              to="/app/jefe/inicio"
              className={`nav-item ${location.pathname === '/app/jefe/inicio' ? 'active' : ''}`}
              title="Inicio"
              data-label="Inicio"
            >
              <FaTachometerAlt />
              <span>Inicio</span>
            </Link>
            <Link
              to="/app/jefe/invitaciones"
              className={`nav-item ${location.pathname === '/app/jefe/invitaciones' ? 'active' : ''}`}
              title="Invitaciones"
              data-label="Invitaciones"
            >
              <FaInbox />
              <span>Invitaciones</span>
              {jefeMetrics?.pendingInvitations > 0 && (
                <span className="nav-badge">{jefeMetrics.pendingInvitations}</span>
              )}
            </Link>
            <Link
              to="/app/jefe/proyectos"
              className={`nav-item ${location.pathname === '/app/jefe/proyectos' ? 'active' : ''}`}
              title="Mis Proyectos"
              data-label="Mis Proyectos"
            >
              <FaProjectDiagram />
              <span>Mis Proyectos</span>
            </Link>
            <Link
              to="/app/jefe/tareas"
              className={`nav-item ${location.pathname === '/app/jefe/tareas' ? 'active' : ''}`}
              title="Tareas"
              data-label="Tareas"
            >
              <FaClipboardCheck />
              <span>Tareas</span>
              {jefeMetrics?.tasksInReview > 0 && (
                <span className="nav-badge">{jefeMetrics.tasksInReview}</span>
              )}
            </Link>
          </>
        )}
      </nav>

      <button onClick={handleLogoutClick} className="logout-button" title="Cerrar Sesión" data-label="Cerrar Sesión">
        <FaSignOutAlt />
        <span>Cerrar Sesión</span>
      </button>

      {showLogoutModal && createPortal(
        <div className="logout-modal-overlay" onClick={handleCancelLogout}>
          <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="logout-modal-icon">
              <FaSignOutAlt />
            </div>
            <h3>¿Cerrar Sesión?</h3>
            <p>¿Estás seguro que deseas cerrar tu sesión?</p>
            <div className="logout-modal-actions">
              <button onClick={handleCancelLogout} className="btn-cancel">Cancelar</button>
              <button onClick={handleConfirmLogout} className="btn-confirm">Sí, Cerrar Sesión</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
